import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronRightOutlined, ExpandMoreOutlined } from '@mui/icons-material'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined'
import { Box, Checkbox, CircularProgress, Collapse, IconButton, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { getProcessTree } from './processApi'
import {
  PROCESS_ROW_DROP_KIND,
  buildProcessPaletteTree,
  censusCoverageOf,
  countProcessOperations,
  isSelectableProcessRow,
  pendingProcessRows,
  selectedProcessRows,
  templateDragPropertiesOf,
  toProcessDropPayload,
  toggleProcessRowSelection
} from './processPaletteTree'
import { CHOICE_KIND } from './libraryEffect'
import { processRowLines, processTreeRowLines } from './libraryRowLines'
import { aggregateDurationText, contractDurationMs, operationDurationText, subprocessDurationMs } from './libraryDuration'
import { LibraryBadge, LibraryRow, LibraryTray, LibraryTrayItem, paletteColorOf, writeDragData } from './LibraryListParts'

/**
 * ProcessPaletteSection — one row of the Processes family (story 167), unfoldable into its process tree
 * (story 152, E1). What story 152 decided stays: the tree is asked ONCE (P1), unfolding is read-only, the
 * multiple selection lives inside one tree, a linked JOB is not offered without OPERATION_LIBRARY (RN-G16),
 * and the census and the "Pending to add" filter are the ones of the Operations family (RN-03.8).
 *
 * Story 167 adds: the row format (D16.3), and that clicking the process or one of its rows CHOOSES it —
 * the panel then says what dropping it will do (CA5.1, CA5.4). Choosing a process that is not unfolded asks
 * its tree, the same single load unfolding does, so the text can say how many operations it brings.
 *
 * PO, 29/09/2026: unfolded, the process opens into a TRAY under its card and its rows hang from the tray's rail;
 * a sub-process opens into a tray of its own inside it. What is inside a process reads as inside it.
 *
 * The `dataTransfer` does not change: a whole process travels as `workflow` + id (+ origin pair), one or
 * several JOB as `processJobs` + the JSON of `toProcessDropPayload` in tree order.
 */

/** Los TRES estados de color de la cobertura (`RN-03.5`, S74): todo presente · parcial · ninguno. */
const COVERAGE_LOOK = {
  ALL: { state: 'ALL', color: 'success.main' },
  PARTIAL: { state: 'PARTIAL', color: 'yellow.main' },
  NONE: { state: 'NONE', color: 'error.main' }
}

export const resolveCoverageLook = ({ present = 0, total = 0 } = {}) => {
  if (total > 0 && present >= total) {
    return COVERAGE_LOOK.ALL
  }
  return present > 0 ? COVERAGE_LOOK.PARTIAL : COVERAGE_LOOK.NONE
}

/**
 * S74/S78/S81 — "presentes / total" with its three colours; a subtree with an unread branch is PARTIAL. It is a
 * gauge: the text, and under it a bar that fills as the operations come in.
 */
const CoverageIndicator = ({ testId, coverage, t_ }) => {
  const look = resolveCoverageLook(coverage)
  if (coverage.noLibraryOperations) {
    return (
      <Typography variant='caption' color='text.secondary' data-testid={testId} data-coverage='EMPTY'>
        {t_('No library operations in this process')}
      </Typography>
    )
  }
  const filled = coverage.total > 0 ? Math.min(1, coverage.present / coverage.total) : 0
  return (
    <Box data-testid={testId} data-coverage={look.state} sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, minWidth: 0 }}>
      <Typography variant='caption' color={look.color} sx={{ lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' }}>
        {t_('{{present}} of {{total}} operations added', { present: coverage.present, total: coverage.total })}
        {coverage.partial ? ' · ' + t_('This process could not be fully loaded') : ''}
      </Typography>
      <Box
        aria-hidden
        sx={(theme) => {
          const color = paletteColorOf(theme, look.color)
          return {
            height: 4,
            borderRadius: '999px',
            bgcolor: alpha(color, 0.18),
            backgroundImage: 'linear-gradient(90deg, ' + color + ' 0 ' + Math.round(filled * 100) + '%, transparent 0)',
            transition: 'background-image 200ms'
          }
        }}
      />
    </Box>
  )
}

/** Las filas que se pueden marcar, JOB y ramas WORKFLOW, en el orden del árbol (#2990). */
const selectableRows = (rows = [], canReadOperationLibrary = false) => {
  const selectable = []
  const walk = (list) => {
    list.forEach((row) => {
      if (isSelectableProcessRow(row, canReadOperationLibrary)) {
        selectable.push(row)
      }
      walk(row.children || [])
    })
  }
  walk(rows || [])
  return selectable
}

/** The glyph of a row of the tree: what it becomes when dropped — a sub-process, a linked operation or a copy. */
const rowIconOf = (row) => {
  if (row.nodeType === 'WORKFLOW') {
    return AccountTreeOutlinedIcon
  }
  return row.linked ? HandymanOutlinedIcon : ContentCopyOutlinedIcon
}

/** What the glyph of a row means, in words, for its tooltip. */
const rowIconTipOf = (row) => {
  if (row.nodeType === 'WORKFLOW') {
    return 'Sub-process: it comes in as a subworkflow'
  }
  return row.linked
    ? 'Linked operation: it stays linked to the library operation'
    : 'Local copy: a job of this process only, not linked to the library. It comes in as a copy you can edit'
}

const TRAY_FOLD_MS = 240

const TREE_STATUS = { IDLE: 'IDLE', LOADING: 'LOADING', LOADED: 'LOADED', FAILED: 'FAILED' }

const ProcessPaletteSection = ({
  process = {},
  dragProperties = templateDragPropertiesOf(process),
  disabled = false,
  canReadOperationLibrary = false,
  census = undefined,
  pendingOnly = false,
  showDuration = false,
  chosenKey = '',
  onChoose = () => {},
  onItemDragStart = () => {},
  onItemDragEnd = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const [expanded, setExpanded] = useState(false)
  const [branches, setBranches] = useState({})
  const [checked, setChecked] = useState({})
  const [tree, setTree] = useState({})
  const [treeStatus, setTreeStatus] = useState(TREE_STATUS.IDLE)
  // PO, 29/09/2026: a tray unfolds downwards and folds back up; while it is on screen its card stays squared.
  const [shownTrays, setShownTrays] = useState({})
  const trayMotion = (key) => ({
    timeout: TRAY_FOLD_MS,
    mountOnEnter: true,
    unmountOnExit: true,
    onEnter: () => setShownTrays((current) => ({ ...current, [key]: true })),
    onExited: () => setShownTrays((current) => ({ ...current, [key]: false }))
  })

  const processId = process?.id || 0
  const unreadableProcess = treeStatus === TREE_STATUS.FAILED

  const loadTree = useCallback(async () => {
    setTreeStatus(TREE_STATUS.LOADING)
    try {
      setTree(buildProcessPaletteTree(await getProcessTree(processId)))
      setTreeStatus(TREE_STATUS.LOADED)
    } catch (thrown) {
      // S6/S7: un proceso que no se puede leer se DECLARA con el mismo texto que una rama que no se
      // pudo cargar; nunca desaparece de la paleta en silencio (`RN-01.3b`). No se vuelve a pedir.
      setTreeStatus(TREE_STATUS.FAILED)
    }
  }, [processId])

  /** #152 S4 · CA5.1: asked once — the first time the process is unfolded or chosen — and never again. */
  const ensureTree = useCallback(() => {
    if (treeStatus === TREE_STATUS.IDLE) {
      loadTree()
    }
  }, [treeStatus, loadTree])

  const toggleProcess = useCallback((event) => {
    event.stopPropagation()
    ensureTree()
    setExpanded((current) => !current)
  }, [ensureTree])

  // Historia 152 (S74 · CA3.2): con censo hay cifras; sin él —sin `OPERATION_LIBRARY`— el árbol es el
  // mismo árbol, navegable y arrastrable, solo que SIN NÚMEROS (`RN-03.10`, S89).
  const showCensus = !!census?.countOf

  const filteredRows = useMemo(
    () => (showCensus && pendingOnly ? pendingProcessRows(tree.rows || [], census) : (tree.rows || [])),
    [showCensus, pendingOnly, tree.rows, census]
  )
  const coverage = useMemo(
    () => (showCensus ? censusCoverageOf(tree.rows || [], census) : null),
    [showCensus, tree.rows, census]
  )
  const coverageOfRow = useCallback(
    (row) => (showCensus && row.nodeType === 'WORKFLOW' && !row.unreadable ? censusCoverageOf(row.children || [], census) : null),
    [showCensus, census]
  )

  const selectable = useMemo(() => selectableRows(filteredRows, canReadOperationLibrary), [filteredRows, canReadOperationLibrary])
  const selectableIds = useMemo(() => new Set(selectable.map((row) => row.nodeId)), [selectable])
  // "Select all" marca las OPERACIONES del árbol, no sus ramas: sigue soltando filas JOB (#152 S24, #2990).
  const selectableJobs = useMemo(() => selectable.filter((row) => row.nodeType === 'JOB'), [selectable])
  const allChecked = selectableJobs.length > 0 && selectableJobs.every((row) => checked[row.nodeId])

  const processOperationCount = useMemo(() => countProcessOperations(tree.rows || []), [tree.rows])
  const processChoice = useMemo(() => ({
    kind: CHOICE_KIND.PROCESS,
    key: 'process:' + processId,
    label: process?.name || process?.code || '',
    name: process?.name || process?.code || '',
    operationCountKnown: treeStatus === TREE_STATUS.LOADED && processOperationCount.known,
    operationCount: processOperationCount.count
  }), [processId, process, treeStatus, processOperationCount])

  // CA5.1 / RN-07.2: the text follows the tree — without figure while it travels, with it once it is here.
  useEffect(() => {
    if (chosenKey === processChoice.key) {
      onChoose(processChoice)
    }
  }, [chosenKey, processChoice, onChoose])

  const rowChoiceOf = useCallback((row) => {
    const key = 'process-row:' + processId + ':' + row.nodeId
    if (row.nodeType === 'WORKFLOW') {
      const count = countProcessOperations(row.children || [])
      return { kind: CHOICE_KIND.PROCESS, key, label: row.name || row.code, name: row.name || row.code, operationCountKnown: !row.unreadable && count.known, operationCount: count.count }
    }
    if (row.linked) {
      return { kind: CHOICE_KIND.PROCESS_LINKED_JOB, key, label: row.name || row.operationCode, code: row.operationCode }
    }
    return { kind: CHOICE_KIND.PROCESS_LOCAL_JOB, key, label: row.name || row.code, code: row.code }
  }, [processId])

  const selectionChoiceOf = useCallback((marked) => ({
    kind: CHOICE_KIND.PROCESS_SELECTION,
    key: 'process-selection:' + processId,
    label: (marked[0]?.name || marked[0]?.code || '') + (marked.length > 1 ? ' +' + (marked.length - 1) : ''),
    count: marked.length
  }), [processId])

  /** CA5.4 / S79: ticking says "Creates N nodes"; a single tick says what that one row creates. */
  const changeChecked = (nextChecked) => {
    setChecked(nextChecked)
    // What would travel, each row once: a ticked branch goes as its whole sub-process and its rows are not
    // broken down (#2990); an operation reached twice in the tree is one tick.
    const marked = [...new Map(selectedProcessRows(filteredRows, nextChecked, canReadOperationLibrary)
      .map((row) => [row.nodeId, row])).values()]
    if (marked.length > 1) {
      onChoose(selectionChoiceOf(marked))
    }
    if (marked.length === 1) {
      onChoose(rowChoiceOf(marked[0]))
    }
  }

  /**
   * S24 — dragging a ticked row drops the whole selection, in tree order; an unticked row, only itself. A
   * ticked branch travels as its whole sub-process (#2990).
   */
  const draggedRowsOf = useCallback(
    (row) => (checked[row.nodeId] ? selectedProcessRows(filteredRows, checked, canReadOperationLibrary) : [row]),
    [checked, filteredRows, canReadOperationLibrary]
  )

  const dragPropertiesOf = useCallback((row) => {
    if (disabled) {
      return false
    }
    const payload = toProcessDropPayload(row, tree)
    if (!payload.kind) {
      return false
    }
    if (payload.kind === PROCESS_ROW_DROP_KIND.PROCESS && checked[row.nodeId]) {
      return {
        'application/reactflow/nodeType': 'processJobs',
        'application/reactflow/subType': JSON.stringify(draggedRowsOf(row).map((candidate) => toProcessDropPayload(candidate, tree)))
      }
    }
    if (payload.kind === PROCESS_ROW_DROP_KIND.PROCESS) {
      return {
        'application/reactflow/nodeType': 'workflow',
        'application/reactflow/subType': String(payload.subWorkflowId),
        // D12: el par entero, con la versión que declara la RAÍZ del árbol desplegado (P1).
        'application/reactflow/sourceProcessCode': payload.sourceProcessCode,
        'application/reactflow/sourceProcessVersionNumber': payload.sourceProcessVersionNumber
      }
    }
    if (payload.kind === PROCESS_ROW_DROP_KIND.LINKED_JOB && !canReadOperationLibrary) {
      return false
    }
    return {
      'application/reactflow/nodeType': 'processJobs',
      'application/reactflow/subType': JSON.stringify(draggedRowsOf(row).map((candidate) => toProcessDropPayload(candidate, tree)))
    }
  }, [canReadOperationLibrary, checked, disabled, draggedRowsOf, tree])

  /**
   * D12 (`RN-01.10b` punto 2): el par sale del listado de plantillas y, **si el árbol ya está cargado, del
   * árbol** — las dos fuentes describen la misma fila; si alguna vez discrepasen, manda la del árbol.
   */
  const processDragProperties = disabled || dragProperties === false
    ? false
    : {
        ...dragProperties,
        'application/reactflow/sourceProcessCode': tree.code || process?.code || '',
        'application/reactflow/sourceProcessVersionNumber': tree.code
          ? tree.processVersionNumber
          : (dragProperties?.['application/reactflow/sourceProcessVersionNumber'] ?? process?.version ?? null)
      }

  const chooseProcess = () => {
    onChoose(processChoice)
    ensureTree()
  }

  const dragProcess = (event) => {
    writeDragData(event, processDragProperties)
    ensureTree()
    onItemDragStart(processChoice)
  }

  const dragRow = (event, row) => {
    const properties = dragPropertiesOf(row)
    if (!properties) {
      return
    }
    writeDragData(event, properties)
    const dragged = draggedRowsOf(row)
    onItemDragStart(dragged.length > 1 ? selectionChoiceOf(dragged) : rowChoiceOf(row))
  }

  // D9 (C1): what the whole process lasts, as the template list publishes it.
  const cardLines = processRowLines(process, showDuration ? aggregateDurationText(contractDurationMs(process?.totalTimeMs)) : '')
  const rowDurationTextOf = (row) => {
    if (!showDuration) {
      return ''
    }
    return row.nodeType === 'WORKFLOW' ? aggregateDurationText(subprocessDurationMs(row)) : operationDurationText(row.timeMs)
  }
  // PO, 29/09/2026 (the mockup's card): how many operations the process brings — counted on the tree once it is
  // here, the figure of the template list until then (Sun counts it the same way).
  const operationCount = treeStatus === TREE_STATUS.LOADED
    ? (processOperationCount.known ? processOperationCount.count : null)
    : (Number.isInteger(process?.operationCount) ? process.operationCount : null)
  const operationsFact = (count, testId) => ({ key: 'operations', testId, text: count === null ? '' : t_('{{count}} operations', { count }) })

  const renderRow = (row, index, level) => {
    const rowDragProperties = dragPropertiesOf(row)
    const lines = processTreeRowLines(row, rowDurationTextOf(row))
    const rowCoverage = coverageOfRow(row)
    const isBranch = row.nodeType === 'WORKFLOW' && !row.unreadable
    const branchOpen = isBranch && branches[row.nodeId] === true
    const branchCount = isBranch ? countProcessOperations(row.children || []) : null
    const facts = row.nodeType === 'WORKFLOW'
      ? [operationsFact(branchCount && branchCount.known ? branchCount.count : null, 'process-row-operations-' + row.nodeId)]
      : [{ key: 'technology', testId: 'process-row-technology-' + row.nodeId, text: row.technologyName }]
    return (
      <LibraryTrayItem key={row.nodeId} last={index === level.length - 1}>
        <LibraryRow
          testId={'process-row-' + row.nodeId}
          secondaryTestId={(row.linked ? 'process-row-operation-' : 'process-row-secondary-') + row.nodeId}
          primary={lines.primary}
          secondary={lines.secondary}
          icon={rowIconOf(row)}
          iconTip={t_(rowIconTipOf(row))}
          iconTestId={'process-row-glyph-' + row.nodeId}
          facts={facts}
          nested
          open={branchOpen || shownTrays[row.nodeId] === true}
          chosen={chosenKey === 'process-row:' + processId + ':' + row.nodeId}
          draggable={rowDragProperties !== false}
          disabled={disabled}
          onClick={() => onChoose(rowChoiceOf(row))}
          onDragStart={(event) => dragRow(event, row)}
          onDragEnd={onItemDragEnd}
          leading={
            <>
              {isBranch
                ? (
                  <IconButton
                    size='small'
                    data-testid={'process-row-expand-' + row.nodeId}
                    aria-label={t_('Expand process')}
                    aria-expanded={branchOpen}
                    title={t_('Expand process')}
                    sx={{ p: 0.25 }}
                    onClick={(event) => {
                      event.stopPropagation()
                      setBranches((current) => ({ ...current, [row.nodeId]: !current[row.nodeId] }))
                    }}
                  >
                    {branchOpen ? <ExpandMoreOutlined fontSize='inherit' /> : <ChevronRightOutlined fontSize='inherit' />}
                  </IconButton>
                  )
                : null}
              {selectableIds.has(row.nodeId)
                ? (
                  <Checkbox
                    size='small'
                    sx={{ p: 0.25 }}
                    inputProps={{ 'data-testid': 'process-row-check-' + row.nodeId }}
                    checked={checked[row.nodeId] === true}
                    onChange={(event) => changeChecked(toggleProcessRowSelection(filteredRows, checked, row.nodeId, event.target.checked, canReadOperationLibrary))}
                    onClick={(event) => event.stopPropagation()}
                  />
                  )
                : null}
            </>
          }
        >
          {/* S74: la marca de presencia de un JOB VINCULADO ya traído; un JOB local no la lleva nunca (S75). */}
          {showCensus && census.isPresent(row)
            ? <LibraryBadge tone='success' icon={<CheckCircleOutlineIcon />} testId={'process-row-present-' + row.nodeId}>{t_('Already in this workflow')}</LibraryBadge>
            : null}
          {row.unreadable
            ? <LibraryBadge tone='error'>{t_('This process could not be fully loaded')}</LibraryBadge>
            : null}
          {/* A folded sub-process says how far it is covered on its card; unfolded, its tray says it. */}
          {rowCoverage && !branchOpen
            ? <CoverageIndicator testId={'process-row-coverage-' + row.nodeId} coverage={rowCoverage} t_={t_} />
            : null}
        </LibraryRow>
        {isBranch
          ? (
            <Collapse in={branchOpen} {...trayMotion(row.nodeId)}>
              <LibraryTray
                nested
                testId={'process-row-tray-' + row.nodeId}
                header={rowCoverage ? <CoverageIndicator testId={'process-row-coverage-' + row.nodeId} coverage={rowCoverage} t_={t_} /> : null}
              >
                {/* S83: un subproceso que el filtro deja sin filas SE DECLARA vacío (`RN-03.8b`). */}
                {row.filteredEmpty
                  ? <Typography variant='caption' color='text.secondary' data-testid={'process-row-empty-' + row.nodeId}>{t_('No operations pending to add')}</Typography>
                  : (row.children || []).map((child, childIndex, siblings) => renderRow(child, childIndex, siblings))}
              </LibraryTray>
            </Collapse>
            )
          : null}
      </LibraryTrayItem>
    )
  }

  const trayOpen = expanded && treeStatus !== TREE_STATUS.IDLE

  return (
    <Stack data-testid={'process-palette-' + processId}>
      <LibraryRow
        testId={'process-palette-card-' + processId}
        secondaryTestId={'process-palette-card-secondary-' + processId}
        primary={cardLines.primary}
        secondary={cardLines.secondary}
        icon={AccountTreeOutlinedIcon}
        facts={[operationsFact(operationCount, 'process-palette-card-operations-' + processId)]}
        open={trayOpen || shownTrays.process === true}
        chosen={chosenKey === processChoice.key}
        draggable={processDragProperties !== false}
        disabled={disabled}
        onClick={chooseProcess}
        onDragStart={dragProcess}
        onDragEnd={onItemDragEnd}
        leading={
          <IconButton
            size='small'
            data-testid={'process-palette-expand-' + processId}
            aria-label={t_('Expand process')}
            aria-expanded={expanded}
            title={t_('Expand process')}
            sx={{ p: 0.25 }}
            onClick={toggleProcess}
          >
            {expanded ? <ExpandMoreOutlined fontSize='inherit' /> : <ChevronRightOutlined fontSize='inherit' />}
          </IconButton>
        }
      />
      <Collapse in={trayOpen} {...trayMotion('process')}>
        <LibraryTray
          testId={'process-palette-tray-' + processId}
          header={
            <>
              {treeStatus === TREE_STATUS.LOADING
                ? <CircularProgress size={16} data-testid={'process-palette-loading-' + processId} sx={{ alignSelf: 'center' }} />
                : null}
              {unreadableProcess
                ? <Typography variant='caption' color='error'>{t_('This process could not be fully loaded')}</Typography>
                : null}
              {coverage && treeStatus === TREE_STATUS.LOADED
                ? <CoverageIndicator testId={'process-palette-coverage-' + processId} coverage={coverage} t_={t_} />
                : null}
              {selectableJobs.length > 0
                ? (
                  <Stack direction='row' alignItems='center' sx={{ ml: -0.5 }}>
                    <Checkbox
                      size='small'
                      sx={{ p: 0.5 }}
                      inputProps={{ 'data-testid': 'process-palette-select-all-' + processId }}
                      checked={allChecked}
                      onChange={(event) => changeChecked(event.target.checked ? Object.fromEntries(selectableJobs.map((row) => [row.nodeId, true])) : {})}
                    />
                    <Typography variant='caption'>{t_('Select all')}</Typography>
                  </Stack>
                  )
                : null}
            </>
            }
        >
          {filteredRows.map((row, index, level) => renderRow(row, index, level))}
        </LibraryTray>
      </Collapse>
    </Stack>
  )
}

export default React.memo(ProcessPaletteSection)
